import { useEffect } from 'react';

export type MarketplaceInstallChangeKind = 'installed' | 'updated' | 'removed';

export interface MarketplaceInstallChange {
  readonly kind: MarketplaceInstallChangeKind;
  readonly workspaceId: string;
  readonly templateId?: string;
}

const CHANNEL_NAME = 'streamlain.marketplace.install-state.v1';
const SAME_WINDOW_EVENT = 'streamlain:marketplace-install-state';

function isMarketplaceInstallChange(
  value: unknown
): value is MarketplaceInstallChange {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<MarketplaceInstallChange>;
  return (
    (candidate.kind === 'installed' ||
      candidate.kind === 'updated' ||
      candidate.kind === 'removed') &&
    typeof candidate.workspaceId === 'string' &&
    candidate.workspaceId.length > 0 &&
    (candidate.templateId === undefined ||
      typeof candidate.templateId === 'string')
  );
}

/**
 * Fast invalidation signal only. Consumers always re-read the authoritative
 * install state from the server after receiving it.
 */
export function publishMarketplaceInstallChange(
  change: MarketplaceInstallChange
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<MarketplaceInstallChange>(SAME_WINDOW_EVENT, {
      detail: change,
    })
  );
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage(change);
  channel.close();
}

export function subscribeMarketplaceInstallChanges(
  listener: (change: MarketplaceInstallChange) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handleWindowEvent = (event: Event) => {
    const change = (event as CustomEvent<unknown>).detail;
    if (isMarketplaceInstallChange(change)) listener(change);
  };
  window.addEventListener(SAME_WINDOW_EVENT, handleWindowEvent);

  const channel =
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(CHANNEL_NAME);
  if (channel) {
    channel.onmessage = event => {
      if (isMarketplaceInstallChange(event.data)) listener(event.data);
    };
  }

  return () => {
    window.removeEventListener(SAME_WINDOW_EVENT, handleWindowEvent);
    channel?.close();
  };
}

export function useMarketplaceInstallStateRefresh(
  workspaceId: string | null | undefined,
  refresh: () => void | Promise<unknown>
): void {
  useEffect(() => {
    if (!workspaceId || typeof window === 'undefined') return;
    const revalidate = () => {
      void refresh();
    };
    const unsubscribe = subscribeMarketplaceInstallChanges(change => {
      if (change.workspaceId === workspaceId) revalidate();
    });
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', revalidate);
    window.addEventListener('pageshow', revalidate);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      unsubscribe();
      window.removeEventListener('focus', revalidate);
      window.removeEventListener('pageshow', revalidate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh, workspaceId]);
}

export function buildMarketplaceTemplateUrl(input: {
  readonly workspaceId: string;
  readonly templateId: string;
  readonly templateType: string;
  readonly targetCustomAppId?: string;
  readonly targetRootRoutePath?: string;
}): string {
  const params = new URLSearchParams({
    type: input.templateType,
    detail: input.templateId,
  });
  if (input.targetCustomAppId) {
    params.set('targetCustomAppId', input.targetCustomAppId);
  }
  if (input.targetRootRoutePath) {
    params.set('targetRootRoutePath', input.targetRootRoutePath);
  }
  return `/workspace/${encodeURIComponent(input.workspaceId)}/marketplace?${params.toString()}`;
}

export function openMarketplaceTemplateInNewTab(input: {
  readonly workspaceId: string;
  readonly templateId: string;
  readonly templateType: string;
  readonly targetCustomAppId?: string;
  readonly targetRootRoutePath?: string;
}): void {
  if (typeof window === 'undefined') return;
  window.open(
    buildMarketplaceTemplateUrl(input),
    '_blank',
    'noopener,noreferrer'
  );
}
